
import { useMapStore } from "@/store/useMapStore";
import { CustomizerConfig, SizeConfig } from "@/config/customizerConfig";
import { sizeIdToDisplay } from "@/config/customizerConfig";

interface SizeTabProps {
  config: CustomizerConfig;
}

// Proportional visual box sizes matching the HTML spec
const visualSizeMap: Record<string, { w: string; h: string }> = {
  xs: { w: "w-8", h: "h-10" },
  small: { w: "w-10", h: "h-12" },
  medium: { w: "w-12", h: "h-14" },
  large: { w: "w-14", h: "h-16" },
};

// Subtitle labels per size
const subtitleMap: Record<string, string> = {
  xs: "Extra Small",
  small: "Small",
  medium: "Medium",
  large: "Large",
};

// Fallback prices from HTML spec (used when no Shopify variant pricing available)
const fallbackPriceMap: Record<string, string> = {
  xs: "$55",
  small: "$95",
  medium: "$149",
  large: "$249",
};

const SizeTab = ({ config }: SizeTabProps) => {
  const { selectedOptions, selectOption, variants } = useMapStore();

  // Get price for a size from Shopify variants or fallback
  const getPriceForSize = (sizeId: string): string => {
    const shopifyDisplay = sizeIdToDisplay[sizeId];
    if (shopifyDisplay && variants.length > 0) {
      const matchingVariant = variants.find((v) =>
        v.selectedOptions.some(
          (opt) => opt.name === "Size" && opt.value === shopifyDisplay
        )
      );
      if (matchingVariant) {
        const price = parseFloat(matchingVariant.price);
        return `$${price % 1 === 0 ? price.toFixed(0) : price.toFixed(2)}`;
      }
    }
    return fallbackPriceMap[sizeId] || "";
  };

  // Shopify uses letter x with no spaces
  const getDimensions = (size: { dimensions: string; id: string }): string => {
    // Convert config format to display format matching spec: 4"x5", 8"x10", etc.
    const display = sizeIdToDisplay[size.id];
    return display || size.dimensions;
  };

  return (
    <div className="p-6 space-y-6">
      <h3
        className="text-lg mb-4"
        style={{ fontFamily: "var(--font-heading)", color: "var(--brand-text)" }}
      >
        Choose Size
      </h3>

      <div className="space-y-3">
        {config.sizes.map((size) => {
          const isSelected = selectedOptions.Size === size.id;
          const visual = visualSizeMap[size.id] || { w: "w-10", h: "h-12" };

          return (
            <div
              key={size.id}
              onClick={() => selectOption("Size", size.id)}
              className="flex items-center justify-between p-4 rounded-xl cursor-pointer transition-colors"
              style={{
                fontFamily: "var(--font-body)",
                border: isSelected
                  ? "2px solid var(--sage-400)"
                  : "1px solid var(--brand-border)",
                backgroundColor: isSelected ? "var(--sage-50)" : "transparent",
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = "var(--sage-300)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = "var(--brand-border)";
                }
              }}
            >
              {/* Left: visual box + text */}
              <div className="flex items-center gap-3">
                <div
                  className={`${visual.w} ${visual.h} rounded-sm`}
                  style={{
                    border: `1px solid ${isSelected ? "var(--sage-400)" : "var(--sage-300)"}`,
                    backgroundColor: isSelected ? "var(--sage-100)" : "var(--sage-50)",
                  }}
                />
                <div>
                  <span
                    className="text-sm font-medium block"
                    style={{ color: "var(--brand-text)" }}
                  >
                    {getDimensions(size)}
                  </span>
                  <span
                    className="text-xs"
                    style={{ color: "var(--brand-muted)" }}
                  >
                    {subtitleMap[size.id] || size.name}
                  </span>
                </div>
              </div>

              {/* Right: price + optional badge */}
              <div className="text-right">
                <span
                  className="font-medium block"
                  style={{ color: "var(--sage-400)" }}
                >
                  {getPriceForSize(size.id)}
                </span>
                {size.id === "small" && (
                  <span
                    className="text-[10px] text-white px-2 py-0.5 rounded-full inline-block mt-1"
                    style={{ backgroundColor: "var(--sage-400)" }}
                  >
                    Popular
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SizeTab;
